import { Component, Input, Output, EventEmitter, OnInit, ViewChild, ElementRef, AfterViewInit, inject, ChangeDetectionStrategy } from '@angular/core';
import { HisSearchService } from '../../genericsearch/service/his-search.service';
import { HisOption } from '../../genericsearch/model/his-option';

@Component( {
    selector: 'his-search-select-autocomplete',
    templateUrl: './his-search-select-autocomplete.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
} )
export class HisSearchSelectAutocompleteComponent implements OnInit, AfterViewInit {
    private searchService = inject(HisSearchService);


    // unique id for html
    @Input() uid: string;

    // filter selected Options
    @Input() removeSelectedOptionsFromDropdown = false;

    // selectedOptions to be able to filter out already selectedOptions from autocomplete
    @Input() selectedOptions: HisOption[];
    // the name of the search entity
    @Input() searchEntity: string;
    // the search configuration file, required for rights validation on the backend
    @Input() searchConfigFile: string;

    // minimum length of autocomplete input to trigger a search
    @Input() minLength = 1;
    // maximum number of suggestions to show in autocomplete (null -> show all)
    @Input() limit: number = null;
    // show icon to open dropdown?
    @Input() showDropdownIcon = true;
    // show null item in autocomplete select list?
    @Input() showNullItem = false;

    @Input() isFirstInputElement = false;
    @ViewChild( 'inputElement' ) inputElement: ElementRef;

    // suggestions for autocomplete
    filteredSelectList: HisOption[] = [];
    // element selected by autocomplete
    selectedElement: HisOption;
    // previous autocomplete input string
    lastAutocompleteQuery: string = undefined;

    @Output() selected: EventEmitter<HisOption> = new EventEmitter();

    ngOnInit() {
        if ( this.minLength === 0 ) {
            // we need suggestions from the start
            let requestedCount: string = ( this.limit ) ? this.limit.toString() : 'all';
            console.log( 'HisSearchSelectAutocompleteComponent.ngOnInit(): Request ' + requestedCount + ' options of entity',
                this.searchEntity, 'from backend...' );
            this.loadFilteredOptions( '' );
            this.lastAutocompleteQuery = '';
        }
    }

    ngAfterViewInit() {
        // Set focus on first input element
        if ( this.isFirstInputElement ) {
            setTimeout( () => this.inputElement.nativeElement.focus() );
        }
    }

    /**
     * Find dropdown suggestions. The event should only fire if the autocomplete input length >= minLength
     *
     * @param $event event
     */
    filterSelectList( $event: { query: string } ) {
        let autocompleteQuery = $event.query;
        console.log( 'HisSearchSelectAutocompleteComponent.filterSelectList(): event =', $event, ', query =', autocompleteQuery );
        // Produce filteredSelectList:
        // * If the new query contains the previous one, then we filter the last filtering result again
        // * If the new query does not contain the last one (because some characters were removed from the
        //   autocomplete input field), then we have access the backend
        // * Case is ignored.
        console.log( 'HisSearchSelectAutocompleteComponent.filterSelectList(): Old filteredSelectList =',
            this.filteredSelectList, ', lastAutocompleteQuery =', this.lastAutocompleteQuery );
        if ( this.lastAutocompleteQuery && autocompleteQuery.indexOf( this.lastAutocompleteQuery ) > -1 ) {
            let nextList: HisOption[] = [];
            let listElem: HisOption;
            for ( listElem of this.filteredSelectList ) {
                if ( listElem.label.toUpperCase().indexOf( autocompleteQuery.toUpperCase() ) > -1 ) {
                    nextList.push( listElem );
                }
            }
            this.filteredSelectList = nextList;
            console.log( 'HisSearchSelectAutocompleteComponent.filterSelectList(): Filtered options on client, new filteredSelectList =',
                this.filteredSelectList );
        } else {
            console.log( 'HisSearchSelectAutocompleteComponent.filterSelectList(): Request options of entity',
                this.searchEntity, 'with defaulttext like *' + autocompleteQuery + '* from backend...' );
            this.loadFilteredOptions( autocompleteQuery );
        }

        this.lastAutocompleteQuery = autocompleteQuery;
    }

    private loadFilteredOptions( autocompleteQuery: string ) {
        this.searchService.loadFilteredOptions( this.searchConfigFile, autocompleteQuery, this.limit ).subscribe(
            ( result: HisOption[] ) => {
                if ( result ) {
                    this.filteredSelectList = result;
                    console.log( 'HisSearchSelectAutocompleteComponent.loadFilteredOptions(): ' +
                    'filteredSelectList obtained from backend =',
                    this.filteredSelectList );
                }
            }
        );
    }

    private filterSelectedOptions( result: HisOption[] ): HisOption[] {
        //only filter if is set to true
        if ( !this.removeSelectedOptionsFromDropdown ) {
            return result;
        }

        let selectedOptions = this.selectedOptions;
        let filteredResult: HisOption[] = [];
        for ( let i = 0; result.length > i; i++ ) {

            if ( !this.isElementAlreadySelected( result[i].id ) ) {
                filteredResult.push( result[i] );
            }
        }
        return filteredResult;
    }

    // same function as in search-select, but basically they do differnet things:
    private isElementAlreadySelected( id: string ): boolean {
        // console.log( 'id =', id, 'has type', typeof id );
        for ( let option of this.selectedOptions ) {
            let optionId: string | number = option.id;
            // TODO According to class HisOption the option.id should be of string type
            // But obviously the search result obtained from the REST service contains number ids...
            // console.log( 'optionId =', optionId, 'has type', typeof optionId );
            if ( id === optionId.toString() ) {
                return true;
            }
        }
        return false;
    }

    private limitResult( result: HisOption[] ): HisOption[] {
        if ( this.limit != null && result.length > this.limit ) {
            result = result.slice( 0, this.limit );
        }
        return result;
    }

    /**
     * Pass element selected to wrapping component.
     */
    emitSelectedElement() {
        this.selected.emit( this.selectedElement );
    }
}
